import { Box, IconButton, Stack } from "@mui/material";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { Board } from "@common-models/board";
import { DBBoardsThunks, DBWorkspaceThunks, boardsSelectors, BoardsSelectPaginationStatus } from "@common-reducers/DBServiceThunks";
import Lottie from "@/common/components/lazy-lottie/lazy-lottie";
import NoResults from '@common-assets/lottie/no-results.json';
import BoardListItem from "./board-list-item";
import classes from "./boards-panel.module.scss";
import { useCallback, useRef, useState, useEffect } from "react";
import { getIsLoadingBoards, isCurrentWorkspaceOwnerSelector } from "@common-reducers/BoardsSelectors";
import { useSelector } from "react-redux";
import { boardsActions } from "@common-reducers/BoardsReducer";
import BTInputField from "@common-components/bt-input-field/bt-input-field";
import SearchIcon from '@mui/icons-material/Search';
import { ClearIcon } from '@mui/x-date-pickers/icons';
import { BiSearch } from "react-icons/bi";
import { useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";
import { useRouter } from "@common-reducers/hooks/use-router";
import { addNewBoardThunk } from "@common-reducers/BoardsThunks";
import { taskActions } from "@common-reducers/TasksReducer";
import getRandomEmoji from 'get-random-emoji';
import RowStack from "@common/components/common/row-stack/RowStack";
import { CircularProgress } from '@mui/material';
import { selectUserId } from "@common-reducers/UserSelectors";

interface LazyLoadingBoardsListProps {
    drawerIsOpen: boolean;
    height?: number;
}

const INITIAL_LIMIT = 20;
const LOAD_MORE_LIMIT = 10;

// Sortable Board Item Component
function SortableBoardItem({ board, isDragDisabled }: { board: Board; isDragDisabled: boolean }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: board._id ?? '',
        disabled: isDragDisabled,
    });

    const style = {
        transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
        zIndex: isDragging ? 1000 : 'auto',
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
        >
            <BoardListItem
                provided={null}
                isDragging={isDragging}
                boardId={board._id ?? ''}
            />
        </div>
    );
}

function LazyLoadingBoardsList({ drawerIsOpen, height }: LazyLoadingBoardsListProps) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const router = useRouter();
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasEverSearched, setHasEverSearched] = useState(false); // מעקב אחר חיפוש
    const [isSearching, setIsSearching] = useState(false); // מצב חיפוש פעיל
    const [localBoards, setLocalBoards] = useState<Board[]>([]); // state מקומי לסדר
    const [isDragging, setIsDragging] = useState(false); // מצב גרירה פעיל
    const searchInputRef = useRef<HTMLInputElement>();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Minimum distance before drag starts
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Board state
    const ownerId = useAppSelector(selectUserId);
    const boardsFilter = useAppSelector((state) => state.BoardsReducer.boardsFilterName);
    const isBoardsLoading = useAppSelector(getIsLoadingBoards);
    const isCurrentWorkspaceOwner = useAppSelector(isCurrentWorkspaceOwnerSelector);
    const deletedBoardName = useAppSelector(state => state.TasksReducer.deletedBoardName);
    const boardsList: Board[] = useSelector((state) => boardsSelectors.selectAll(state));
    const paginationStatus = useAppSelector((state) => BoardsSelectPaginationStatus(state.DBBoardsReducer, {}));
    // קבל את הבורדים ישירות מהשרת (ללא client-side filtering)
    const serverBoards = useAppSelector((state) => {
        // השתמש בכל הבורדים שהשרת החזיר ללא סינון נוסף!
        return boardsSelectors.selectAll(state);
    });

    const hasActiveSearch = Boolean(boardsFilter && boardsFilter.trim());
    // During search, render only search results.
    // Without search, render server boards (except while dragging local reordered list).
    const filteredBoards = hasActiveSearch
        ? localBoards
        : (isDragging && localBoards.length > 0 ? localBoards : serverBoards);


    // Handle search filter changes
    useEffect(() => {
        // אם אין חיפוש ולא באמצע גרירה, נקה את ה-localBoards כדי להציג את כל הנתונים
        if ((!boardsFilter || boardsFilter.trim() === '') && !isSearching && !isDragging) {
            setLocalBoards([]);
        }
    }, [boardsFilter, isSearching, isDragging]); // עדכן כשהחיפוש או מצב החיפוש/גרירה משתנים

    // Board initialization logic
    const createFirstBoard = useCallback(async () => {
        dispatch(taskActions.setCreatingFirstBoardStatus("creating"));
        const res: any = await dispatch(addNewBoardThunk({
            boardName: "new board",
            emoji: getRandomEmoji(),
            template: ''
        }));
        const boardId = res.payload;
        dispatch(taskActions.setCreatingFirstBoardStatus(""));
        router.navigate(`/tasks/?boardID=${boardId}`);
    }, [dispatch, router]);

    useEffect(() => {
        // לא ליצור בורד חדש אם אי פעם חיפשנו או שיש חיפוש פעיל!
        const isSearching = boardsFilter && boardsFilter.trim().length > 0;
        
        if (!isBoardsLoading && boardsList.length === 0 && !deletedBoardName && isCurrentWorkspaceOwner && !isSearching && !hasEverSearched) {
            createFirstBoard();
        }
    }, [createFirstBoard, isBoardsLoading, boardsList.length, deletedBoardName, isCurrentWorkspaceOwner, boardsFilter, hasEverSearched]);

    // Initial load with 20 boards
    useEffect(() => {
        const loadBoards = async () => {
            try {
                await dispatch(DBBoardsThunks.find({
                    deleted: false,
                    $limit: INITIAL_LIMIT,
                    $skip: 0,
                    $sort: { order: -1 }
                })).unwrap();
            } catch (error) {
                console.error('Initial load failed:', error);
            }
        };
        loadBoards();
        dispatch(DBWorkspaceThunks.find({}));
    }, [dispatch]);

    // Reset pagination when filter changes
    useEffect(() => {
        setHasMore(true);
        // החיפוש מתבצע כעת ישירות בשרת בפונקציית filterBoards
    }, [boardsFilter]);

    // Sync hasMore with pagination status after data loads
    // This ensures hasMore is set to false when we've loaded all items
    useEffect(() => {
        if (paginationStatus.total > 0 && !isLoadingMore && !isSearching) {
            const currentSkip = boardsList.length;
            // If we've loaded all items, set hasMore to false
            if (currentSkip >= paginationStatus.total) {
                setHasMore(false);
            }
        }
    }, [paginationStatus.total, boardsList.length, isLoadingMore, isSearching]);

    // Load more boards when scrolling near bottom
    const loadMoreBoards = useCallback(async () => {
        if (isLoadingMore || !hasMore) return;
        
        // Check if we've already loaded all items
        const currentSkip = boardsList.length;
        const totalItems = paginationStatus.total;
        
        // If we've reached or exceeded the total, stop loading
        if (totalItems > 0 && currentSkip >= totalItems) {
            setHasMore(false);
            return;
        }
        
        setIsLoadingMore(true);
        try {
            const serverQuery: any = {
                deleted: false,
                owner: ownerId,
                $limit: LOAD_MORE_LIMIT,
                $skip: boardsList.length, // Use total boards count, not filtered
                $sort: { order: -1 }
            };
            
            // הוסף את מונח החיפוש אם קיים
            if (boardsFilter && boardsFilter.trim()) {
                serverQuery.name = { $regex: boardsFilter, $options: 'i' };
            }
            
            const result: any = await dispatch(DBBoardsThunks.find(serverQuery));
            
            // Check response to determine if there are more items
            const responseTotal = result.payload?.total ?? 0;
            const responseSkip = result.payload?.skip ?? 0;
            const responseData = result.payload?.data ?? [];
            
            // Stop loading if:
            // 1. We got fewer results than requested, OR
            // 2. The skip value is >= total (we've reached the end)
            if (responseData.length < LOAD_MORE_LIMIT || (responseTotal > 0 && responseSkip >= responseTotal)) {
                setHasMore(false);
            }
        } catch {
            setHasMore(false); // Stop trying to load more on error
        } finally {
            setIsLoadingMore(false);
        }
    }, [dispatch, boardsList.length, isLoadingMore, hasMore, boardsFilter, ownerId, paginationStatus.total]);

    // Scroll handler to detect when near bottom
    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current) return;
        
        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        const threshold = 100; // Load more when 100px from bottom
        
        if (scrollHeight - scrollTop - clientHeight < threshold) {
            loadMoreBoards();
        }
    }, [loadMoreBoards]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    // Cleanup search state on unmount (navigation/logout/component removal)
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = null;
            }
            dispatch(boardsActions.setBoardsFilterName(''));
        };
    }, [dispatch]);

    // Debounced search function
    const performSearch = useCallback(async (searchTerm: string) => {
        setHasMore(true); // Reset hasMore when filtering
        setHasEverSearched(true); // סמן שחיפשנו
        
        // חיפוש ישיר בשרת במקום בstore
        const serverQuery: any = {
            deleted: false,
            owner: ownerId,
            $limit: INITIAL_LIMIT,
            $skip: 0,
            $sort: { order: -1 }
        };
        
        // אם יש טקסט חיפוש, הוסף אותו לשאילתה
        if (searchTerm && searchTerm.trim()) {
            serverQuery.name = { $regex: searchTerm, $options: 'i' };
        }
        
        try {
            // שלח שאילתה לשרת והמתן לתשובה
            const result = await dispatch(DBBoardsThunks.find(serverQuery)).unwrap();
            
            // Handle both response formats: array directly or object with data property
            const searchResults: Board[] = Array.isArray(result) 
                ? result 
                : (result?.data || []);
            
            // Set localBoards to show ONLY search results (empty if no matches)
            // This is what the user sees in the UI - selected board won't show if it doesn't match
            if (searchTerm && searchTerm.trim()) {
                setLocalBoards(searchResults);
            } else {
                setLocalBoards([]);
            }
        } catch {
        } finally {
            // סיים את מצב החיפוש רק אחרי שהתשובה הגיעה
            setIsSearching(false);
        }
    }, [dispatch, ownerId]);

    function filterBoards({ target }: any) {
        const searchTerm = target.value;
        
        // עדכן את הstate מיד כדי שהשדה יראה את הטקסט
        dispatch(boardsActions.setBoardsFilterName(searchTerm));
        
        // נקה את ה-timeout הקודם אם קיים
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        // אם יש טקסט חיפוש, הגדר מצב loading
        if (searchTerm && searchTerm.trim()) {
            setIsSearching(true);
        } else {
            setIsSearching(false);
        }
        
        // הגדר timeout חדש ל-300ms - גם לחיפוש וגם למחיקה
        searchTimeoutRef.current = setTimeout(() => {
            performSearch(searchTerm);
        }, 300);
    }

    async function clearSearch() {
        // נקה את ה-timeout אם קיים
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = null;
        }
        
        dispatch(boardsActions.setBoardsFilterName(''));
        setHasMore(true); // Reset hasMore when clearing search
        setHasEverSearched(true); // סמן שחיפשנו (גם כשמנקים)
        
        // נקה את ה-localBoards מיד כדי להציג את כל הבורדים
        setLocalBoards([]);
        
        try {
            // טען מחדש את כל הבורדים מהשרת ללא פילטר
            await dispatch(DBBoardsThunks.find({
                deleted: false,
                $limit: INITIAL_LIMIT,
                $skip: 0,
                $sort: { order: -1 }
            })).unwrap();
        } catch {
        } finally {
            // סיים מצב חיפוש רק אחרי שהתשובה הגיעה
            setIsSearching(false);
        }
    }

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        
        if (!over || active.id === over.id) {
            setIsDragging(false);
            return;
        }

        const oldIndex = filteredBoards.findIndex(board => board._id === active.id);
        const newIndex = filteredBoards.findIndex(board => board._id === over.id);

        // Create new array with moved item
        const newBoards = arrayMove(filteredBoards, oldIndex, newIndex);

        // עדכן את ה-localBoards מיד כדי שהמשתמש יראה את השינוי
        setLocalBoards(newBoards);
        setIsDragging(true); // סמן שאנחנו באמצע גרירה

        // Since the server expects order in ascending order (0, 1, 2...)
        // but displays them in descending order (-1 sort), we need to reverse the indices
        const changes = newBoards.map((board, index) => ({
            id: board._id,
            changes: {
                order: newBoards.length - 1 - index  // Reverse the order for server
            }
        }));

        // Update all boards with new order
        dispatch(DBBoardsThunks.patchMany(changes)).then(() => {
            // טען מחדש את הנתונים מהשרת עם הסדר החדש
            dispatch(DBBoardsThunks.find({
                deleted: false,
                owner: ownerId,
                $limit: INITIAL_LIMIT,
                $skip: 0,
                $sort: { order: -1 }
            })).then(() => {
                // אחרי שהנתונים החדשים הגיעו, נקה את ה-localBoards
                setLocalBoards([]);
                setIsDragging(false);
            });
        }).catch(() => {
            // במקרה של שגיאה, חזור לסדר המקורי
            setLocalBoards([]);
            setIsDragging(false);
        });
    }

    return (
        <Stack sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <RowStack sx={{ padding: "0 22px" }} justifyContent={"center"} >
                <BTInputField
                sx={{ width: "100%" }}
                    onClick={drawerIsOpen ? () => { } : undefined}
                    className={classes.search_input}
                    type="text"
                    ref={searchInputRef}
                    value={boardsFilter ?? ""}
                    onChange={filterBoards}
                    placeholder={drawerIsOpen ? t('boardsPanel.searchPlaceholder') : ""}
                    startAdornment={drawerIsOpen ? <SearchIcon sx={{ color: 'var(--tasks-text-secondary)' }} /> : ""}
                    endAdornment={
                        boardsFilter && (
                            <IconButton size='small' style={{ fontSize: '12px' }} onClick={clearSearch}>
                                <ClearIcon style={{ fontSize: '14px' }} />
                            </IconButton>
                        )
                    }
                />
                <div style={{ opacity: drawerIsOpen ? 0 : 1 }}
                    className={classes.customSearchIcon}>
                    <BiSearch />
                </div>
            </RowStack>

            {/* Initial loading indicator */}
            {isBoardsLoading && (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '32px',
                    flexGrow: 1
                }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Search loading indicator */}
            {isSearching && (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '32px',
                    flexGrow: 1
                }}>
                    <CircularProgress size={24} />
                    <Box sx={{ ml: 2, color: 'var(--tasks-text-secondary)' }}>
                        Searching...
                    </Box>
                </Box>
            )}

            {!isBoardsLoading && !isSearching && filteredBoards.length > 0 ? (
                <div 
                    ref={scrollContainerRef}
                    style={{ 
                        marginTop: 16, 
                        overflowY: 'auto', 
                        overflowX: 'hidden', 
                        flexGrow: 1 
                    }} 
                    className={classes.scrollContainer}
                >
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={() => setIsDragging(true)}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={filteredBoards.map(board => board._id)}
                            strategy={verticalListSortingStrategy}
                        >
                            <div style={{ minHeight: '100%' }}>
                                {filteredBoards.map((board) => (
                                    <SortableBoardItem
                                        key={board._id}
                                        board={board}
                                        isDragDisabled={isLoadingMore || isSearching}
                                    />
                                ))}
                                
                                {/* Loading indicator for more items */}
                                {isLoadingMore && (
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        padding: '16px'
                                    }}>
                                        <CircularProgress size={20} />
                                    </Box>
                                )}
                            </div>
                        </SortableContext>
                    </DndContext>
                </div>
            ) : (
                !isBoardsLoading && !isSearching && (
                    <Stack px={5} sx={{ flexGrow: 1 }} justifyContent={"center"} alignItems={"center"}>
                        <Lottie autoPlay={true} loop={true} animationData={NoResults} style={{ width: '200px', height: '200px' }} />
                        <Box fontSize="16px" textAlign={"center"}>
                            {boardsFilter && boardsFilter.trim() 
                                ? "No boards found matching your search. Try a different search term or add a new board! ✨"
                                : "We've looked everywhere but couldn't find matching boards. Maybe it's hiding? Try a different search term or add new board!✨"
                            }
                        </Box>
                    </Stack>
                )
            )}
        </Stack>
    );
}

export default LazyLoadingBoardsList;
